import {isNotNullOrUndefined} from '@wandb/weave/common/util/types';
import {type AlignedData} from 'uplot';

import {type SeriesEntry} from './getSeriesEntries';

const FUNCTION_PERFORMANCE_SAMPLE_RATE = 0.05;
export type UPlotAlignmentRumContext = {
  /** Stable panel ID within the page, not a title or metric name. */
  panelId: string;
  /** Rendered Y series, including band boundaries and original overlays. */
  seriesCount: number;
  /** Shared X-union length; every aligned Y array has this length. */
  xValueCount: number;
  /** Sum of source array lengths, including duplicates and filtered points. */
  inputPointCount: number;
  /** Longest source array; the ideal shared length for unique, unfiltered X. */
  maxInputPointCount: number;
  /** seriesCount * xValueCount: allocated Y slots including padding, not bytes. */
  alignedYSlotCount: number;
  /** maxInputPointCount / xValueCount: 1 means no shared-grid expansion. */
  alignmentEfficiency: number;
  /** alignedYSlotCount / inputPointCount: total Y storage expansion. */
  ySlotAmplification: number;
  /** Input lengths precede log filtering; exclude these events for ratio analysis. */
  xLogScale: boolean;
  /** Fraction of this chart's observed size changes selected for reporting. */
  sampleRate: 0.05;
  /** Unchanged total Y sizes do not report, even when source values change. */
  samplingUnit: 'chart-aligned-size-change';
  /** Existing alignment implementation label, not an efficiency classification. */
  alignmentPath: 'general';
};

export type AlignmentMeasurement = {
  alignedYSlotCount: number;
  sample?: {
    startTime: number;
    duration: number;
    context: Omit<UPlotAlignmentRumContext, 'panelId'>;
  };
};

/** Capture synchronous alignment time without emitting telemetry during render. */
export function getProfiledAlignedData(
  seriesEntries: (SeriesEntry | null)[],
  xLogScale?: boolean
): {data: AlignedData; alignmentMeasurement: AlignmentMeasurement} {
  const sampled = Math.random() < FUNCTION_PERFORMANCE_SAMPLE_RATE;
  const startMark = sampled ? performance.now() : 0;
  const data = getAlignedData(seriesEntries, xLogScale);
  const duration = sampled ? performance.now() - startMark : 0;
  const seriesCount = data.length - 1;
  const xValueCount = data[0].length;
  const alignedYSlotCount = seriesCount * xValueCount;
  const alignmentMeasurement: AlignmentMeasurement = {alignedYSlotCount};
  if (sampled && alignedYSlotCount > 0) {
    let inputPointCount = 0;
    let maxInputPointCount = 0;
    for (const entry of seriesEntries) {
      if (entry != null) {
        inputPointCount += entry.line.data.length;
        maxInputPointCount = Math.max(
          maxInputPointCount,
          entry.line.data.length
        );
      }
    }
    if (inputPointCount > 0) {
      alignmentMeasurement.sample = {
        startTime: performance.timeOrigin + startMark,
        duration,
        context: {
          alignmentPath: 'general',
          sampleRate: FUNCTION_PERFORMANCE_SAMPLE_RATE,
          samplingUnit: 'chart-aligned-size-change',
          inputPointCount,
          maxInputPointCount,
          alignedYSlotCount,
          alignmentEfficiency: maxInputPointCount / xValueCount,
          ySlotAmplification: alignedYSlotCount / inputPointCount,
          seriesCount,
          xValueCount,
          xLogScale: xLogScale === true,
        },
      };
    }
  }
  return {data, alignmentMeasurement};
}

export function getAlignedData(
  seriesEntries: (SeriesEntry | null)[],
  xLogScale?: boolean
): AlignedData {
  // Collect all unique x values from all lines and sort them
  const allXValues = new Set<number>();
  for (const entry of seriesEntries) {
    if (entry == null) {
      continue;
    }
    for (const point of entry.line.data) {
      const xValue = point.x;
      // Log scale cannot handle 0 or negative values - filter them out
      if (xLogScale && xValue <= 0) {
        continue;
      }
      allXValues.add(xValue);
    }
  }
  const sortedX = Array.from(allXValues).sort((a, b) => a - b);

  const xValueCount = sortedX.length;
  // Iterate through the sorted x values once to create a map of x -> index
  // to allow fast lookup of where the y value should be stored in the yArrays
  // for a given x.
  const xValueToIndex = new Map<number, number>();
  for (let i = 0; i < xValueCount; i++) {
    xValueToIndex.set(sortedX[i], i);
  }

  // For each line, create a y-values array aligned to sortedX
  // Use null where the line doesn't have data for that x value
  const yArrays = seriesEntries
    .map(entry => {
      if (entry == null) {
        return null;
      }
      const {type, line} = entry;
      // oxlint-disable-next-line unicorn/no-new-array -- https://oxc.rs/docs/guide/usage/linter/rules/unicorn/no-new-array.html
      const yValues = new Array<number | null>(xValueCount).fill(null);
      for (const point of line.data) {
        const index = xValueToIndex.get(point.x);
        if (index != null) {
          if (type === 'band-max') {
            yValues[index] = point.y0 ?? null;
          } else {
            yValues[index] = point.y;
          }
        }
      }
      return yValues;
    })
    .filter(isNotNullOrUndefined);

  return [sortedX, ...yArrays];
}
